import React from 'react';
import { Col, Row } from 'reactstrap';
import OrderTransactionDto, { TransactionStatus } from '../../Common/dtos/OrderTransactionDto';
import { toAbbreviation } from '../../Common/Units';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import BoldButton from '../../Common/BoldButton';
import VoidOrderTransactionModal from './VoidOrderTransactionModal';
import OverflowTooltip from '../../Common/OverflowTooltip';

interface OrderTransactionViewProps {
  orderTransaction: OrderTransactionDto;
  setOrderTransactionToVoid?: (orderTransaction: OrderTransactionDto | null) => void;
  handleVoidSuccess: () => void;
}

const OrderTransactionView = (props: OrderTransactionViewProps) => {
  const { orderTransaction } = props;
  const [voidModalOpen, setVoidModalOpen] = React.useState<boolean>(false);

  const displayBlendName = (orderTransaction: OrderTransactionDto, i: number) => orderTransaction.blends?.length > 1 ? `Blend ${i + 1}` : "Blend";

  const cityStatePostalString = (city: string | undefined, state: string | undefined, postalCode: string | undefined): string | undefined => {
    let formattedString: string | undefined = undefined;

    city !== undefined ? formattedString = city : formattedString = undefined;

    state !== undefined ?
      formattedString !== undefined ?
        formattedString += ", " + state :
        formattedString = state :
      formattedString = undefined;

    postalCode !== undefined ?
      formattedString !== undefined ?
        formattedString += " " + postalCode :
        formattedString = postalCode :
      formattedString = undefined;

    return formattedString;
  }

  const TransportDisplayString = (orderTransaction: OrderTransactionDto, measurementCompartmentIndex?: number): string => {
    let formattedString: string = "";

    if (measurementCompartmentIndex === undefined) {
      return formattedString;
    }

    const transportCompartment = orderTransaction.compartments.at(measurementCompartmentIndex);
    const transport = transportCompartment?.transportRef !== undefined ? orderTransaction.transports.find(t => t.transportRef === transportCompartment?.transportRef) : undefined;

    formattedString = transportCompartment?.transportCompartmentIndex !== undefined ? "Compartment " + (transportCompartment.transportCompartmentIndex + 1) + " of\n" : "";
    formattedString += transport !== undefined ? transport?.name : "";

    return formattedString;
  }

  const VoidButton = (props: { onClick?: () => void, disabled?: boolean }) => {
    return <BoldButton
      className="btn-ghost-danger"
      disabled={props.disabled}
      onClick={props.onClick}
    >
      Void Transaction
    </BoldButton>
  }

  const handleVoidClicked = () => {
    props.setOrderTransactionToVoid?.(orderTransaction);
    setVoidModalOpen(true);
  }

  const handleVoidModalClose = () => {
    props.setOrderTransactionToVoid?.(null);
    setVoidModalOpen(false);
  }

  return <>
    <React.Fragment key={`orderTransaction-${orderTransaction.id}`}>
      <div className="d-flex justify-content-center row">
        <Col xs={4} />
        <h3 className="flex-grow-1 text-center col-4 mb-0">
          {orderTransaction.status === TransactionStatus.Voided ? 'Voided ' : ''}
          Order Transaction {orderTransaction.number}
        </h3>
        <Col xs={4} className="text-end mt-n3">
          {
            orderTransaction.partnerBusinessName
              ? null
              : orderTransaction.status === TransactionStatus.Voided
                ? <OverlayTrigger
                  overlay={
                    <Tooltip>
                      Transaction is already voided
                    </Tooltip>
                  }
                >
                  <span>
                    <VoidButton disabled={true} />
                  </span>
                </OverlayTrigger>
                : <VoidButton onClick={handleVoidClicked} />
          }
        </Col>
      </div>
      <p className="d-flex justify-content-center fw-bold">Order Number: {orderTransaction.orderNumber}</p>
      <p className="d-flex justify-content-center">Loaded at: {new Date(orderTransaction.loadedAt).toLocaleString()}</p>
      <div className="border p-5 mb-5">
        <Row className="text-black">
          <Row className="fw-bold">
            <Col>Customer Information:</Col>
            <Col>Destination Information:</Col>
            <Col>Site Information:</Col>
            <Col>Ticket Notes:</Col>
          </Row>
          <Row className="mb-5">
            <Col>
              {orderTransaction.customer.name}<br />
              {orderTransaction.customer.street}<br />
              {cityStatePostalString(orderTransaction.customer.city, orderTransaction.customer.state, orderTransaction.customer.postalCode)}<br />
              {orderTransaction.customer.country}
            </Col>
            <Col>
              {orderTransaction.destination?.name}<br />
              {orderTransaction.destination?.street}<br />
              {cityStatePostalString(orderTransaction.destination?.city, orderTransaction.destination?.state, orderTransaction.destination?.postalCode)}<br />
              {orderTransaction.destination?.country}
            </Col>
            <Col>
              {orderTransaction.siteName}
            </Col>
            <Col>
              <OverflowTooltip
                lines="3"
                text={orderTransaction.ticketNotes} />
            </Col>
          </Row>
          <Row className="fw-bold mt-3">
            <Col>Branch Information:</Col>
            <Col>Applicator Information:</Col>
            <Col>Carrier Information:</Col>
            <Col>{orderTransaction.status === TransactionStatus.Voided && 'Voided By:'}</Col>
          </Row>
          <Row>
            <Col>
              {orderTransaction.branch?.name}<br />
              {orderTransaction.branch?.number}
            </Col>
            <Col>
              {orderTransaction.applicator?.name}<br />
              {orderTransaction.applicator?.license}<br />
              {orderTransaction.applicator?.epaNumber}
            </Col>
            <Col>
              {orderTransaction.carrier?.name}<br />
              {orderTransaction.carrier?.number}
            </Col>
            <Col>
              {orderTransaction.status === TransactionStatus.Voided &&
                <>
                  {orderTransaction.voidedInfo?.voidedByUserEmail}<br />
                  on {new Date(orderTransaction.voidedInfo?.voidedAt!).toLocaleString()}<br />
                  {orderTransaction.voidedInfo?.reason
                    ? <OverflowTooltip placement="auto" text={`Reason: ${orderTransaction.voidedInfo.reason}`} lines="3" />
                    : <span><em>No reason provided</em></span>
                  }
                </>
              }
            </Col>
          </Row>
        </Row>
        {
          orderTransaction.blends.map((blend, bi) =>
            <div key={`blends-${orderTransaction.id}-${bi}`}>
              <h4 className="mt-5"><u>{displayBlendName(orderTransaction, bi)} Information:</u></h4>
              <Row className="text-black fw-bold">
                <Col className="pe-5">Product:</Col>
                <Col>Amount:</Col>
                <Col>Legal For Trade:</Col>
                <Col>Density:</Col>
                <Col>Transport(s):</Col>
                <Col>Compartment(s):</Col>
                <Col />
              </Row>
              {blend.items.map((item, ii) =>
                <React.Fragment key={`items-${blend.id}-${ii}`}>
                  <Row className="my-3 border-top" data-bs-toggle="collapse" data-bs-target={`#measurements-${orderTransaction.id}-${bi}-${ii}`}>
                    <Col className="pe-5 text-black">{item.itemName}</Col>
                    <Col className="text-black">
                      <OverlayTrigger
                        placement="right"
                        overlay={<Tooltip>
                          {parseFloat(item.amount).toLocaleString("en-US", { maximumFractionDigits: 20 })} {toAbbreviation(item.unit)}
                        </Tooltip>}>
                        <span>{item.amount.toLocaleString()} {toAbbreviation(item.unit)}</span>
                      </OverlayTrigger>
                    </Col>
                    <Col />
                    <Col />
                    <Col />
                    <Col />
                    <Col>
                      <a data-bs-toggle="collapse" href={`#measurements-${orderTransaction.id}-${bi}-${ii}`} aria-expanded="false" aria-controls={`measurements-${orderTransaction.id}-${bi}-${ii}`}>
                        <h3 className="icon-arrow-down"><i className="bi-caret-down-square"></i></h3>
                        <h3 className="icon-arrow-up"><i className="bi-caret-up-square"></i></h3>
                      </a>
                    </Col>
                  </Row>
                  <div className="collapse" id={`measurements-${orderTransaction.id}-${bi}-${ii}`}>
                      {item.measurements.map((measurement, mi) =>
                        <Row key={`measurements-${orderTransaction.id}-${bi}-${ii}-${mi}`} className="tree-row tree-row-order-transaction">
                          <Col className="pb-6">{measurement.ingredientName}</Col>
                          <Col>{parseFloat(measurement.amount).toLocaleString("en-US", { maximumFractionDigits: 20 })} {toAbbreviation(measurement.unit)}</Col>
                          <Col>{item.measurements.every(m => m.legalForTrade) ? "Yes" : "No"}</Col>
                          <Col>{measurement.density !== undefined ? `${measurement.density?.value} ${toAbbreviation(measurement.density?.massUnit)}/${toAbbreviation(measurement.density?.volumeUnit)}` : "N/A"}</Col>
                          <Col>{TransportDisplayString(orderTransaction, measurement.compartmentIndex)}</Col>
                          <Col>{measurement.compartmentIndex !== undefined ? "Compartment " + (measurement.compartmentIndex + 1) : ""}</Col>
                          <Col />
                        </Row>)}
                  </div>
                </React.Fragment>
              )}
            </div>
          )
        }
      </div>
    </React.Fragment>
    {voidModalOpen && (
      <VoidOrderTransactionModal
        onHide={handleVoidModalClose}
        orderTransactionId={orderTransaction.id}
        orderTransactionNumber={orderTransaction.number}
        onSuccess={props.handleVoidSuccess}
      />
    )}
  </>
}

export { OrderTransactionView };
